/** Student filters for the management {@link DataTable}. */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { GraduationCap, BookOpen, Baby, Heart, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { SearchInput } from '@/components/forms/SearchInput';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { Select } from '@/components/forms/Select';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { DateRangeInput } from '@/components/forms/DateRangeInput';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useEstablishmentsAutocomplete } from '@/hooks/useEstablishmentsAutocomplete';
import {
  EXIT_REASON_OPTIONS,
  STUDENT_ACCOMPANIMENT_TYPE_CONFIG,
  STUDENT_STATUS_CONFIG,
} from '@/components/students/student-configs';
import type { StudentAccompanimentType, StudentFilters } from '@/types/inperson/student.types';
import { useLevelsSelect } from '@/hooks/useLevelsSelect';
import { useSubjectsSelect } from '@/hooks/useSubjectsSelect';
import { useSchoolClassesSelect } from '@/hooks/useSchoolClassesSelect';
import { getSpecialties } from '@/services/specialties.service';
/** Props for {@link StudentFiltersContent}. */
interface StudentFiltersContentProps {
  filters: StudentFilters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  sessionAvailabilityDay?: number;
  onSetSearch?: (value: string | undefined) => void;
  onToggleStatus: (status: string) => void;
  onToggleGradeLevel: (levelId: string) => void;
  onToggleSubject: (subjectId: string) => void;
  onSetEstablishment: (id: string | undefined) => void;
  onSetClass: (classId: string | undefined) => void;
  onSetSpecialties?: (ids: string[]) => void;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
  onToggleIsMinor: (value: boolean | undefined) => void;
  onToggleHasSpecialNeeds: (value: boolean | undefined) => void;
  onToggleAccompanimentType?: (value: StudentAccompanimentType) => void;
  onSetExitDateRange?: (from: Date | undefined, to: Date | undefined) => void;
  onSetExitReason?: (reason: string | undefined) => void;
  onToggleAvailability?: (day: string) => void;
  initialEstablishmentOption?: { value: string; label: string };
}

/** Filter group is allowed by RBAC and currently toggled on in the configure panel. */
function isVisible(
  filterId: string,
  allowedFilterIds: string[],
  visibleFilterIds: string[],
): boolean {
  return allowedFilterIds.includes(filterId) && visibleFilterIds.includes(filterId);
}

/** Calendar date as YYYY-MM-DD, or undefined when missing. */
function toDateString(d?: Date): string | undefined {
  if (!d) return undefined;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Status-config entries used to render the in-person status chips. */
const STUDENT_INPERSON_STATUS_ENTRIES = Object.entries(STUDENT_STATUS_CONFIG);

/** Debounced first-name / last-name / display-name search; empty string becomes `undefined`. */
const SearchFilter: React.FC<{
  filters: StudentFilters;
  onSetSearch: (value: string | undefined) => void;
}> = ({ filters, onSetSearch }) => {
  function handleSearch(query: string): void {
    onSetSearch(query.trim() || undefined);
  }

  function handleClear(): void {
    onSetSearch(undefined);
  }

  return (
    <div className="filter-group">
      <span className="filter-group-label">Recherche</span>
      <div className="w-64">
        <SearchInput
          size="sm"
          shortcut={false}
          debounceMs={300}
          placeholder="Nom ou prénom..."
          value={filters.search ?? ''}
          onSearch={handleSearch}
          onClear={handleClear}
        />
      </div>
    </div>
  );
};

/** Account-status chips for in-person student filters. */
const StatusFilter: React.FC<{
  filters: StudentFilters;
  onToggleStatus: (status: string) => void;
}> = ({ filters, onToggleStatus }) => {
  const locked = STUDENT_INPERSON_STATUS_ENTRIES.length <= 1;

  function renderStatusChip([
    key,
    config,
  ]: (typeof STUDENT_INPERSON_STATUS_ENTRIES)[number]): React.ReactNode {
    const Icon = config.icon;
    const isActive = locked || filters.statuses.includes(key);

    function handleClick(): void {
      if (!locked) onToggleStatus(key);
    }

    return (
      <Button
        key={key}
        variant="custom"
        size="sm"
        icon={Icon}
        iconPosition="left"
        disabled={locked}
        className={cn(
          'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
          isActive ? 'filter-badge-active' : 'filter-badge-inactive',
          locked && 'opacity-60 cursor-not-allowed',
        )}
        style={
          {
            '--filter-color': config.badgeColor ?? 'rgb(var(--status-neutral))',
          } as React.CSSProperties
        }
        onClick={handleClick}
      >
        {config.label}
      </Button>
    );
  }

  return (
    <div className="filter-group">
      <span className="filter-group-label">Statut</span>
      <div className="filter-options">{STUDENT_INPERSON_STATUS_ENTRIES.map(renderStatusChip)}</div>
    </div>
  );
};

/** Grade-level chips for in-person student filters. */
const GradeLevelFilter: React.FC<{
  filters: StudentFilters;
  onToggleGradeLevel: (levelId: string) => void;
  enabled: boolean;
}> = ({ filters, onToggleGradeLevel, enabled }) => {
  const { options: levels, isLoading } = useLevelsSelect({ enabled });

  return (
    <div className="filter-group">
      <span className="filter-group-label">Niveau scolaire</span>
      <div className="filter-options">
        {isLoading ? (
          <span className="text-sm text-muted-foreground">Chargement...</span>
        ) : (
          levels.map((level) => {
            const isActive = filters.levelIds.includes(level.value);
            return (
              <Button
                key={level.value}
                variant="custom"
                size="sm"
                icon={GraduationCap}
                iconPosition="left"
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                  isActive ? 'filter-option-secondary--active' : 'filter-option-secondary',
                )}
                onClick={() => onToggleGradeLevel(level.value)}
              >
                {level.label}
              </Button>
            );
          })
        )}
      </div>
    </div>
  );
};

/** Subject chips for in-person student filters. */
const SubjectFilter: React.FC<{
  filters: StudentFilters;
  onToggleSubject: (subjectId: string) => void;
  enabled: boolean;
}> = ({ filters, onToggleSubject, enabled }) => {
  const { options: subjects, isLoading } = useSubjectsSelect({ enabled });

  return (
    <div className="filter-group">
      <span className="filter-group-label">Matières</span>
      <div className="filter-options">
        {isLoading ? (
          <span className="text-sm text-muted-foreground">Chargement...</span>
        ) : (
          subjects.map((subject) => {
            const isActive = filters.subjects.includes(subject.value);
            return (
              <Button
                key={subject.value}
                variant="custom"
                size="sm"
                icon={BookOpen}
                iconPosition="left"
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                  isActive ? 'filter-option-tertiary--active' : 'filter-option-tertiary',
                )}
                onClick={() => onToggleSubject(subject.value)}
              >
                {subject.label}
              </Button>
            );
          })
        )}
      </div>
    </div>
  );
};

/** School-class select, enabled after an establishment is chosen. */
const ClassFilter: React.FC<{
  filters: StudentFilters;
  onSetClass: (classId: string | undefined) => void;
}> = ({ filters, onSetClass }) => {
  const isEnabled = !!filters.establishmentId;
  const disabledTooltip = "Sélectionnez d'abord un établissement";

  const { options, isLoading, onLoadMore, hasMore } = useSchoolClassesSelect({
    establishmentId: filters.establishmentId,
    enabled: isEnabled,
  });

  const currentValue = filters.classIds[0] ?? '';

  return (
    <div className="filter-group">
      <span className="filter-group-label">Classe</span>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="w-64 block" style={{ pointerEvents: 'auto' }}>
              <Select
                options={options}
                value={currentValue}
                onChange={(val) => onSetClass(val || undefined)}
                onLoadMore={onLoadMore}
                hasMore={hasMore}
                isLoading={isLoading}
                placeholder={isLoading ? 'Chargement...' : 'Toutes les classes'}
                disabled={!isEnabled}
                size="sm"
              />
            </span>
          </TooltipTrigger>
          {!isEnabled && <TooltipContent side="top">{disabledTooltip}</TooltipContent>}
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

/** Specialty multi-select for in-person student filters. */
const SpecialtyFilter: React.FC<{
  filters: StudentFilters;
  onSetSpecialties: (ids: string[]) => void;
}> = ({ filters, onSetSpecialties }) => {
  const { data: specialties = [], isLoading } = useQuery({
    queryKey: ['specialties', 'active'],
    queryFn: () => getSpecialties(true),
  });

  const options = specialties.map((s) => ({ value: s.id, label: s.label }));

  return (
    <div className="filter-group">
      <span className="filter-group-label">Spécialités</span>
      <div className="w-64">
        <MultiSelect
          options={options}
          value={filters.specialtyIds}
          onChange={onSetSpecialties}
          placeholder={isLoading ? 'Chargement...' : 'Toutes les spécialités'}
          inputSize="sm"
        />
      </div>
    </div>
  );
};

/** Establishment autocomplete for in-person student filters. */
const EstablishmentFilter: React.FC<{
  filters: StudentFilters;
  onSetEstablishment: (id: string | undefined) => void;
  initialEstablishmentOption?: { value: string; label: string };
}> = ({ filters, onSetEstablishment, initialEstablishmentOption }) => {
  const { options, isLoading, onSearch, onLoadMore, hasMore } = useEstablishmentsAutocomplete();

  const enrichedOptions = React.useMemo(() => {
    if (!initialEstablishmentOption) return options;
    const alreadyInList = options.some((o) => o.value === initialEstablishmentOption.value);
    return alreadyInList ? options : [initialEstablishmentOption, ...options];
  }, [options, initialEstablishmentOption]);

  return (
    <div className="filter-group">
      <span className="filter-group-label">Établissement</span>
      <div className="w-64">
        <Autocomplete
          placeholder="Rechercher un établissement..."
          options={enrichedOptions}
          value={filters.establishmentId ?? ''}
          onChange={(val) => onSetEstablishment(val || undefined)}
          onSearch={onSearch}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoading={isLoading}
          minSearchLength={2}
          inputSize="sm"
        />
      </div>
    </div>
  );
};

/** Custom registration-date range for in-person student filters. */
const RegistrationDateFilter: React.FC<{
  filters: StudentFilters;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
}> = ({ filters, onSetCustomDateRange }) => {
  const fromStr = toDateString(filters.createdFrom);
  const toStr = toDateString(filters.createdTo);

  const handleFromChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(undefined, filters.createdTo);
      return;
    }
    const [year, month, day] = val.split('-').map(Number);
    const d = new Date(year, month - 1, day, 0, 0, 0);
    onSetCustomDateRange(d, filters.createdTo);
  };

  const handleToChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(filters.createdFrom, undefined);
      return;
    }
    const [year, month, day] = val.split('-').map(Number);
    const d = new Date(year, month - 1, day, 23, 59, 59, 999);
    onSetCustomDateRange(filters.createdFrom, d);
  };

  return (
    <div className="filter-group">
      <span className="filter-group-label">Date d'inscription</span>
      <div className="w-auto">
        <DateRangeInput
          startDate={fromStr}
          endDate={toStr}
          onStartDateChange={handleFromChange}
          onEndDateChange={handleToChange}
          layout="horizontal"
          showConnector={true}
          showPresets={false}
          hidePickerLabels={true}
          compact={true}
        />
      </div>
    </div>
  );
};

/** Minor and special-needs chips for in-person student filters. */
const ProfileFilter: React.FC<{
  filters: StudentFilters;
  showIsMinor: boolean;
  showSpecialNeeds: boolean;
  onToggleIsMinor: (value: boolean | undefined) => void;
  onToggleHasSpecialNeeds: (value: boolean | undefined) => void;
}> = ({ filters, showIsMinor, showSpecialNeeds, onToggleIsMinor, onToggleHasSpecialNeeds }) => {
  return (
    <div className="filter-group">
      <span className="filter-group-label">Profil</span>
      <div className="filter-options">
        {showIsMinor && (
          <Button
            variant="custom"
            size="sm"
            icon={Baby}
            iconPosition="left"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
              filters.isMinor === true ? 'filter-option-primary--active' : 'filter-option-primary',
            )}
            onClick={() => onToggleIsMinor(filters.isMinor === true ? undefined : true)}
          >
            Mineur
          </Button>
        )}
        {showSpecialNeeds && (
          <Button
            variant="custom"
            size="sm"
            icon={Heart}
            iconPosition="left"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
              filters.hasSpecialNeeds === true
                ? 'filter-option-tertiary--active'
                : 'filter-option-tertiary',
            )}
            onClick={() =>
              onToggleHasSpecialNeeds(filters.hasSpecialNeeds === true ? undefined : true)
            }
          >
            Besoins spécifiques
          </Button>
        )}
      </div>
    </div>
  );
};

/** Entries of {@link STUDENT_ACCOMPANIMENT_TYPE_CONFIG} for chip render. */
const ACCOMPANIMENT_TYPE_ENTRIES = Object.entries(STUDENT_ACCOMPANIMENT_TYPE_CONFIG);

/** Accompaniment-type chips for in-person student filters. */
const AccompanimentTypeFilter: React.FC<{
  filters: StudentFilters;
  onToggleAccompanimentType: (value: StudentAccompanimentType) => void;
}> = ({ filters, onToggleAccompanimentType }) => {
  function renderAccompanimentChip([
    key,
    config,
  ]: (typeof ACCOMPANIMENT_TYPE_ENTRIES)[number]): React.ReactNode {
    const type = key as StudentAccompanimentType;
    const Icon = config.icon;
    const isActive = filters.accompanimentTypes.includes(type);

    function handleClick(): void {
      onToggleAccompanimentType(type);
    }

    return (
      <Button
        key={type}
        variant="custom"
        size="sm"
        icon={Icon}
        iconPosition="left"
        className={cn(
          'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
          isActive ? 'filter-badge-active' : 'filter-badge-inactive',
        )}
        style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
        onClick={handleClick}
      >
        {config.label}
      </Button>
    );
  }

  return (
    <div className="filter-group">
      <span className="filter-group-label">Type d&apos;accompagnement</span>
      <div className="filter-options">
        {ACCOMPANIMENT_TYPE_ENTRIES.map(renderAccompanimentChip)}
      </div>
    </div>
  );
};

/** Custom exit-date range for in-person student filters. */
const ExitDateFilter: React.FC<{
  filters: StudentFilters;
  onSetExitDateRange: (from: Date | undefined, to: Date | undefined) => void;
}> = ({ filters, onSetExitDateRange }) => {
  const fromStr = toDateString(filters.exitDateFrom);
  const toStr = toDateString(filters.exitDateTo);

  const handleFromChange = (val: string) => {
    if (!val) {
      onSetExitDateRange(undefined, filters.exitDateTo);
      return;
    }
    const [year, month, day] = val.split('-').map(Number);
    const d = new Date(year, month - 1, day, 0, 0, 0);
    onSetExitDateRange(d, filters.exitDateTo);
  };

  const handleToChange = (val: string) => {
    if (!val) {
      onSetExitDateRange(filters.exitDateFrom, undefined);
      return;
    }
    const [year, month, day] = val.split('-').map(Number);
    const d = new Date(year, month - 1, day, 23, 59, 59, 999);
    onSetExitDateRange(filters.exitDateFrom, d);
  };

  return (
    <div className="filter-group">
      <span className="filter-group-label">Date de désactivation</span>
      <div className="w-auto">
        <DateRangeInput
          startDate={fromStr}
          endDate={toStr}
          onStartDateChange={handleFromChange}
          onEndDateChange={handleToChange}
          layout="horizontal"
          showConnector={true}
          showPresets={false}
          hidePickerLabels={true}
          compact={true}
        />
      </div>
    </div>
  );
};

/** Exit-reason select for in-person student filters. */
const ExitReasonFilter: React.FC<{
  filters: StudentFilters;
  onSetExitReason: (reason: string | undefined) => void;
}> = ({ filters, onSetExitReason }) => (
  <div className="filter-group">
    <span className="filter-group-label">Motif de sortie</span>
    <div className="w-64">
      <Select
        key={filters.exitReason ?? '__empty__'}
        value={filters.exitReason ?? ''}
        onChange={(v) => onSetExitReason(v || undefined)}
        placeholder="Tous les motifs"
        options={[...EXIT_REASON_OPTIONS]}
        size="sm"
      />
    </div>
  </div>
);

/** Weekday availability chips for in-person student filters. */
const AvailabilityFilter: React.FC<{
  filters: StudentFilters;
  sessionAvailabilityDay?: number;
  onToggleAvailability: (day: string) => void;
}> = ({ filters, sessionAvailabilityDay, onToggleAvailability }) => {
  const DAYS = [
    { value: 'monday', label: 'Lun', dayIndex: 1 },
    { value: 'tuesday', label: 'Mar', dayIndex: 2 },
    { value: 'wednesday', label: 'Mer', dayIndex: 3 },
    { value: 'thursday', label: 'Jeu', dayIndex: 4 },
    { value: 'friday', label: 'Ven', dayIndex: 5 },
  ];

  const selectedDays = React.useMemo(() => {
    const raw = filters.availability;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') return [raw];
    return [];
  }, [filters.availability]);

  return (
    <div className="filter-group">
      <span className="filter-group-label">Disponibilité</span>
      <div className="filter-options">
        {DAYS.map((day) => {
          const isActive = selectedDays.includes(day.value);
          const isSessionDay = sessionAvailabilityDay === day.dayIndex;
          const isDisabled = isSessionDay;
          return (
            <Button
              key={day.value}
              variant="custom"
              size="sm"
              icon={Calendar}
              iconPosition="left"
              disabled={isDisabled}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                isActive ? 'filter-option-secondary--active' : 'filter-option-secondary',
                isDisabled && 'opacity-70 cursor-not-allowed',
              )}
              onClick={() => !isDisabled && onToggleAvailability(day.value)}
            >
              {day.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

/** Student filters for the management {@link DataTable}. */
export const StudentFiltersContent: React.FC<StudentFiltersContentProps> = ({
  filters,
  allowedFilterIds,
  visibleFilterIds,
  sessionAvailabilityDay,
  onSetSearch,
  onToggleStatus,
  onToggleGradeLevel,
  onToggleSubject,
  onSetEstablishment,
  onSetClass,
  onSetSpecialties,
  onSetCustomDateRange,
  onToggleIsMinor,
  onToggleHasSpecialNeeds,
  onToggleAccompanimentType,
  onSetExitDateRange,
  onSetExitReason,
  onToggleAvailability,
  initialEstablishmentOption,
}) => {
  const show = (id: string) => isVisible(id, allowedFilterIds, visibleFilterIds);

  return (
    <div className="filter-groups-container">
      {show('filter-search') && onSetSearch && (
        <SearchFilter filters={filters} onSetSearch={onSetSearch} />
      )}
      {show('filter-status') && <StatusFilter filters={filters} onToggleStatus={onToggleStatus} />}
      {show('filter-grade-level') && (
        <GradeLevelFilter
          filters={filters}
          onToggleGradeLevel={onToggleGradeLevel}
          enabled={true}
        />
      )}
      {show('filter-subject') && (
        <SubjectFilter filters={filters} onToggleSubject={onToggleSubject} enabled={true} />
      )}
      {show('filter-establishment') && (
        <EstablishmentFilter
          filters={filters}
          onSetEstablishment={onSetEstablishment}
          initialEstablishmentOption={initialEstablishmentOption}
        />
      )}
      {show('filter-class') && <ClassFilter filters={filters} onSetClass={onSetClass} />}
      {show('filter-specialties') && onSetSpecialties && (
        <SpecialtyFilter filters={filters} onSetSpecialties={onSetSpecialties} />
      )}
      {show('filter-dates') && (
        <RegistrationDateFilter filters={filters} onSetCustomDateRange={onSetCustomDateRange} />
      )}
      {(show('filter-is-minor') || show('filter-special-needs')) && (
        <ProfileFilter
          filters={filters}
          showIsMinor={show('filter-is-minor')}
          showSpecialNeeds={show('filter-special-needs')}
          onToggleIsMinor={onToggleIsMinor}
          onToggleHasSpecialNeeds={onToggleHasSpecialNeeds}
        />
      )}
      {show('filter-accompaniment-type') && onToggleAccompanimentType && (
        <AccompanimentTypeFilter
          filters={filters}
          onToggleAccompanimentType={onToggleAccompanimentType}
        />
      )}
      {show('filter-availability') && onToggleAvailability && (
        <AvailabilityFilter
          filters={filters}
          sessionAvailabilityDay={sessionAvailabilityDay}
          onToggleAvailability={onToggleAvailability}
        />
      )}
      {show('filter-exit-date') && onSetExitDateRange && (
        <ExitDateFilter filters={filters} onSetExitDateRange={onSetExitDateRange} />
      )}
      {show('filter-exit-reason') && onSetExitReason && (
        <ExitReasonFilter filters={filters} onSetExitReason={onSetExitReason} />
      )}
    </div>
  );
};
